// demoApi — thin wrapper around the PUBLIC endpoints of the live-session
// module: demo classes and webinars.
//
// Public calls do NOT use the logged-in user's JWT; they use a guest JWT
// returned by /register. We use a plain axios instance (no auth interceptor)
// so the public endpoints stay anonymous regardless of admin login state.
//
// The two products differ only in their path prefix and their storage
// namespace, so one factory builds both — see `components/DemoClasses/
// sessionKind.js` for the rest of the per-kind vocabulary.

import axios from "axios";
import { getBaseUrl } from "./getBaseUrl";
import { isUnderBaseDomain } from "./brandHost";

const publicAxios = axios.create({
  baseURL: getBaseUrl(),
  headers: { "Content-Type": "application/json" },
});

// Mirror apiClient's custom-domain header so tenant resolution still works
// when the institute is on a custom subdomain (institute resolution on the
// BE relies on this header for some paths).
publicAxios.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname && hostname !== "localhost" && !isUnderBaseDomain(hostname)) {
      config.headers["X-Custom-Domain"] = hostname;
    }
  }
  return config;
});

const withGuest = (token) => ({
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

/**
 * Build the anonymous API for one kind.
 * @param {string} base  `/public/demo` or `/public/webinar`
 */
const makeSessionApi = (base) => ({
  /** The lobby polls this every 10–15s. */
  getSession: (slug) => publicAxios.get(`${base}/sessions/${slug}`),

  /** Returns { guestToken, demoLeadId, sessionStatus, requiresOtp, joinUrl }. */
  register: (slug, body) => publicAxios.post(`${base}/sessions/${slug}/register`, body),

  /**
   * "Who am I here?" — the call a resume link resolves against.
   *
   * Returns { registered, name, isGuest, feedbackSubmitted, attended,
   * sessionStatus }, or 401 when the credential has expired or been revoked.
   * That 401 is the ONLY reliable way to tell a live token from a dead one:
   * a token in localStorage proves nothing about whether it still works, and
   * acting on a dead one used to drop people into a lobby that then failed.
   */
  getMe: (slug, guestToken) =>
    publicAxios.get(`${base}/sessions/${slug}/me`, withGuest(guestToken)),

  /** Returns the meeting join config (Zoom SDK or a Grats Meet grant), or 409 NOT_LIVE. */
  getJoinConfig: (slug, guestToken) =>
    publicAxios.get(`${base}/sessions/${slug}/join-config`, withGuest(guestToken)),

  heartbeat: (slug, guestToken) =>
    publicAxios.post(`${base}/sessions/${slug}/heartbeat`, {}, withGuest(guestToken)),

  postSession: (slug, guestToken, body) =>
    publicAxios.post(`${base}/sessions/${slug}/post-session`, body, withGuest(guestToken)),

  /** The attendee's recording link, once the session has ended and it is shared. */
  getRecording: (slug, guestToken) =>
    publicAxios.get(`${base}/sessions/${slug}/recording`, withGuest(guestToken)),

  /**
   * The attendee's own certificate of participation: `{ certificate,
   * downloadUrl }`, or 404 NOT_ISSUED. `downloadUrl` is short-lived — fetch it
   * when the button is pressed, not on page load.
   */
  getCertificate: (slug, guestToken) =>
    publicAxios.get(`${base}/sessions/${slug}/certificate`, withGuest(guestToken)),

  /** Absolute URL — used as an `href`, so it cannot go through axios. */
  calendarUrl: (slug) => `${getBaseUrl()}${base}/sessions/${slug}/calendar.ics`,
});

/**
 * Public certificate verification — what a certificate's QR code opens.
 * Serves course (`CERT-…`) and participation (`EVT-…`) certificates alike.
 */
export const verifyAnyCertificate = (certNo) =>
  publicAxios.get(`/public/certificates/${encodeURIComponent(certNo)}`);

/** Absolute URL of a participation certificate's PDF — an `href`, not an XHR. */
export const certificateDownloadUrl = (certNo) =>
  `${getBaseUrl()}/public/certificates/${encodeURIComponent(certNo)}/download`;

export const demoApi = makeSessionApi("/public/demo");
export const webinarApi = makeSessionApi("/public/webinar");

/** Pick the API for a kind — the same normalisation rule as the backend. */
export const getSessionApi = (kind) => (kind === "webinar" ? webinarApi : demoApi);

// LocalStorage helpers for the guest token, per kind AND per slug: a person may
// hold a seat at a demo and a webinar at the same time, and clearing one on the
// thank-you page must not sign them out of the other.
const makeGuestStore = (prefix) => {
  const key = (slug) => `${prefix}:${slug}`;
  return {
    save(slug, payload) {
      try { localStorage.setItem(key(slug), JSON.stringify(payload)); } catch {}
    },
    load(slug) {
      try {
        const raw = localStorage.getItem(key(slug));
        return raw ? JSON.parse(raw) : null;
      } catch { return null; }
    },
    clear(slug) {
      try { localStorage.removeItem(key(slug)); } catch {}
    },
  };
};

export const demoGuestStore = makeGuestStore("demo-guest");
export const webinarGuestStore = makeGuestStore("webinar-guest");

/** Pick the store for a kind. */
export const getGuestStore = (kind) =>
  (kind === "webinar" ? webinarGuestStore : demoGuestStore);

export default demoApi;
